'use client';

import type { DailyBonusCardState } from './components/DailyBonusCard';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { useHorizontalDragScroll } from '@/hooks/useHorizontalDragScroll';
import { useIsMobile } from '@/hooks/useIsMobile';
import openModal from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import DailyBonusGuestOverlay from './components/DailyBonusGuestOverlay';
import DailyBonusInformationDrawer from './components/DailyBonusInformationDrawer';
import DailyBonusTimer from './components/DailyBonusTimer';
import DailyBonusSkeleton from './components/Skeleton';
import { useDailyBonusRewards } from './hooks/useDailyBonusRewards';

const DailyBonusCard = lazy(() => import('./components/DailyBonusCard'));
interface Props {
  classNameCards?: string;
}

const DailyBonus = ({ classNameCards }: Props) => {
  const t = useTranslations('daily-bonus');
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const userSession = useAppSelector(state => state.user.userSession);
  const isGuest = !userSession;
  const { cards, isLoading, showTimer } = useDailyBonusRewards();
  const [canScroll, setCanScroll] = useState(false);

  const [isHydrated, setIsHydrated] = useState(false);

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleClickCapture,
    setScrollRef,
    handleDragStart,
    handleScrollNext,
    handleScrollPrev,
  } = useHorizontalDragScroll();

  const [node, setNode] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!node) {
      return;
    }

    const checkScroll = () => setCanScroll(node.scrollWidth > node.clientWidth);
    checkScroll();

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [node]);

  const ref = (node: HTMLDivElement | null) => {
    setNode(node);
    setScrollRef(node);
  };

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getCardsByState = (state: DailyBonusCardState) => (
    cards
      .filter(c => c.state === state)
      .sort((a, b) => a.order - b.order)
  );

  const groupedCards = useMemo(() => {
    const readyCards = getCardsByState('ready');
    const activeCards = getCardsByState('claimed');
    const lockedCards = getCardsByState('locked');

    // INFO: First list might be with ready to claim cards, after active, and last one is locked
    return [...readyCards, ...activeCards, ...lockedCards];
  }, [cards]);

  const handleOpenModal = () => {
    if (isMobile) {
      setIsOpen(true);
      return;
    }
    openModal('daily-bonus');
  };

  if ((!isGuest && isLoading) || !isHydrated) {
    return <DailyBonusSkeleton lengthCards={8} />;
  }

  return (
    <Suspense fallback={<DailyBonusSkeleton lengthCards={8} />}>
      <section className="relative group/daily-wrapper rounded-2xl">
        {
          !isGuest && canScroll && (
            <>
              <button
                type="button"
                onClick={handleScrollPrev}
                className="absolute z-5 -translate-y-1/2 top-[calc(50%--0.125rem)] left-1 opacity-0 transition-opacity duration-300 group-hover/daily-wrapper:opacity-100 size-6 glass-border-card rounded-full flex justify-center items-center bg-c300/10"
              >
                <Icon name="arrow-top" className="-rotate-90 text-light size-4" />
              </button>
              <button
                type="button"
                onClick={handleScrollNext}
                className="absolute z-5 -translate-y-1/2 top-[calc(50%--0.125rem)] right-1 opacity-0 transition-opacity duration-300 group-hover/daily-wrapper:opacity-100 size-6 glass-border-card rounded-full flex justify-center items-center bg-c300/10"
              >
                <Icon name="arrow-top" className="rotate-90 text-light size-4" />
              </button>
            </>
          )
        }

        <div className={cn('relative size-full overflow-hidden rounded-[inherit] bg-daily-bonus-glass shadow-card glass-stroke-border before:z-2!', isGuest && 'bg-c900/85')}>
          {isGuest && <DailyBonusGuestOverlay />}

          <div className={cn('relative z-1', isGuest && 'bg-c900/85')}>
            <div className="flex lg:p-2.5 p-4 items-center md:gap-1 pb-4 lg:pb-3">
              <h3 className="flex-1 text-white leading-5 text-sm font-semibold">{t('daily-reward')}</h3>
              {showTimer && <DailyBonusTimer />}
            </div>

            <div
              ref={ref}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              onClickCapture={handleClickCapture}
              onDragStart={handleDragStart}
              className={cn('overflow-x-auto overflow-y-hidden hide-scrollbar select-none -mt-12 -mb-13 lg:-mb-9.5 pt-12 pb-13 lg:pb-9.5 px-4 -mx-4', canScroll && 'cursor-grab active:cursor-grabbing')}
            >
              <div className={cn('flex relative gap-2 lg:gap-1.5 lg:px-2.5 px-4 min-w-max', classNameCards)}>
                {groupedCards.map(({ id, order, ...cardProps }) => (
                  <DailyBonusCard key={id} index={order} {...cardProps} />
                ))}
              </div>
            </div>

            <div className="lg:block hidden h-35 w-8.5 absolute top-6.5 right-0" style={{ background: 'linear-gradient(90deg, rgba(50, 27, 64, 0) 0%, #331C41 100%)' }} />

            <div className="flex relative z-10 justify-center lg:p-2.5 pt-4 lg:pt-3 p-4">
              <button
                type="button"
                className="group flex items-center gap-1 font-normal text-xs leading-4 text-c300 transition-colors duration-300 md:hover:cursor-pointer md:hover:text-white"
                onClick={handleOpenModal}
                // onTouchStart={handleOpenModal}
              >
                {t('rewards-stack-learn-more')}
                <Icon name="info-payment" className="size-3 text-c300 transition-colors duration-300 md:group-hover:text-white" />
              </button>
            </div>
          </div>

          <DailyBonusInformationDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
      </section>
    </Suspense>
  );
};

export default DailyBonus;
